import makeApiRequest from '../../../api/makeApiRequest';

const mapSnippet = ({ id, attributes }) => ({ id, ...attributes });

const mapSnippetTagsToExistingUserTags = (snippetTags = [], userTags = []) => {
  return snippetTags.map(tag => userTags.find(({ id }) => id === tag.id)).filter(tag => tag);
};

const updateSnippetsWithUserTags = (snippets, userTags) => {
  return snippets.map(snippet => {
    const tags = mapSnippetTagsToExistingUserTags(snippet.tags, userTags);
    return { ...snippet, tags };
  });
};

const removeValueFromTags = tags => {
  return tags.map(({ id }) => ({ id }));
};

let retryCount = 0;

const Snippets = {
  name: 'snippets',
  state: {
    snippets: [],
    quickReplySnippets: [],
  },
  reducers: {
    onCreated(state, snippet) {
      return {
        ...state,
        snippets: [...state.snippets, { ...snippet }],
        quickReplySnippets: [
          snippet.quickReply
            ? [...state.quickReplySnippets, { ...snippet }]
            : state.quickReplySnippets,
        ],
      };
    },
    onUpdated(state, snippet) {
      const snippets = state.snippets.map(item => (item.id === snippet.id ? snippet : item));
      const quickReplySnippets = snippets.filter(snippet => snippet.quickReply);
      return {
        ...state,
        snippets,
        quickReplySnippets,
      };
    },
    onLoaded(state, snippets) {
      const quickReplySnippets = snippets.filter(snippet => snippet.quickReply);
      return {
        ...state,
        snippets,
        quickReplySnippets,
      };
    },
    onDeleted(state, id) {
      return {
        ...state,
        snippets: state.snippets.filter(snippet => snippet.id !== id),
        quickReplySnippets: state.quickReplySnippets.filter(snippet => snippet.id !== id),
      };
    },
    'superuser/onTagUpdated': (state, tag) => {
      const snippets = state.snippets.map(snippet => {
        const tags = snippet.tags.reduce((prev, currTag) => {
          if (currTag.id === tag.attributes.id) {
            prev.push(tag.attributes);
          } else {
            prev.push(currTag);
          }

          return prev;
        }, []);
        return { ...snippet, tags, quickReply: tags.length ? snippet.quickReply : false };
      });

      return {
        ...state,
        snippets,
        quickReplySnippets: snippets.filter(snippet => snippet.quickReply),
      };
    },
    'superuser/onTagDeleted': (state, tagId) => {
      const snippets = state.snippets.map(snippet => {
        const tags = snippet.tags.filter(tag => tagId !== tag.id);
        return { ...snippet, tags, quickReply: tags.length ? snippet.quickReply : false };
      });

      return {
        ...state,
        snippets,
        quickReplySnippets: snippets.filter(snippet => snippet.quickReply),
      };
    },
  },
  messageListeners: {
    TEXT_SNIPPET_CREATED({ payload, state }) {
      const tagsWithValue = mapSnippetTagsToExistingUserTags(
        payload.tags,
        state.superuser.attributes.tags,
      );
      this.onCreated({ ...payload, tags: tagsWithValue });
    },
    TEXT_SNIPPET_UPDATED({ payload, state }) {
      const tagsWithValue = mapSnippetTagsToExistingUserTags(
        payload.tags,
        state.superuser.attributes.tags,
      );
      this.onUpdated({ ...payload, tags: tagsWithValue });
    },
    TEXT_SNIPPET_DELETED({ payload }) {
      this.onDeleted([payload]);
    },
  },
  effects: dispatch => ({
    async load(_, { oidcSession, superuser }) {
      const url = '/snippets';
      const [request] = makeApiRequest({ url, method: 'get' }, oidcSession);
      const { error, data } = await request;
      if (!error) {
        const snippets = data.data.map(snippet => mapSnippet(snippet));
        const userTags = superuser.attributes.tags;
        this.onLoaded(updateSnippetsWithUserTags(snippets, userTags));
      }
    },
    async create({ payload }, { oidcSession, superuser }) {
      const { tags, ...attributes } = payload;
      const userTags = superuser.attributes.tags;

      const body = {
        data: {
          type: 'snippets',
          attributes: { tags: removeValueFromTags(tags), ...attributes },
        },
      };

      const [request] = makeApiRequest(
        { url: '/snippets', method: 'post', data: body },
        oidcSession,
      );
      const { error, data, ...rs } = await request;
      if (!error) {
        retryCount = 0;

        if (payload.files) {
          dispatch.errors.remove({ realm: 'snippets', type: 'sendFile' });
        } else {
          dispatch.errors.remove({ realm: 'snippets', type: 'send' });
        }

        const tagsWithValue = mapSnippetTagsToExistingUserTags(tags, userTags);

        this.onCreated(
          mapSnippet({
            id: data.data.id,
            attributes: { tags: tagsWithValue, ...attributes },
          }),
        );
      } else {
        if (payload.files) {
          if (rs.status === 424 && retryCount < 6) {
            retryCount += 1;
            setTimeout(() => this.create({ payload }), 500);
            dispatch.errors.add({ realm: 'snippets', type: 'sendFile', error });
            return;
          }
          dispatch.errors.add({ realm: 'snippets', type: 'sendFile', error });
        } else {
          dispatch.errors.add({ realm: 'snippets', type: 'send', error });
        }
        throw error;
      }
    },
    async update({ payload }, { oidcSession }) {
      const url = `/snippets/${payload.id}`;
      const { id, tags, ...attributes } = payload;

      const body = {
        data: {
          type: 'snippets',
          id,
          attributes: {
            tags: removeValueFromTags(tags),
            ...attributes,
          },
        },
      };
      const [request] = makeApiRequest({ url, method: 'patch', data: body }, oidcSession);
      const { error, ...rs } = await request;
      if (!error) {
        retryCount = 0;

        if (payload.files) {
          dispatch.errors.remove({ realm: 'snippets', type: 'sendFile' });
        } else {
          dispatch.errors.remove({ realm: 'snippets', type: 'send' });
        }
        this.onUpdated(payload);
      } else {
        if (payload.files) {
          if (rs.status === 424 && retryCount < 6) {
            retryCount += 1;
            setTimeout(() => this.update({ payload }), 500);
            dispatch.errors.add({ realm: 'snippets', type: 'sendFile', error });
            return;
          }
          dispatch.errors.add({ realm: 'snippets', type: 'sendFile', error });
        } else {
          dispatch.errors.add({ realm: 'snippets', type: 'send', error });
        }
        throw error;
      }
    },
    async delete({ id }, { oidcSession }) {
      const url = `/snippets/${id}`;
      const [request] = makeApiRequest({ url, method: 'delete' }, oidcSession);
      const { error } = await request;
      if (error) {
        dispatch.errors.add({ realm: 'snippets', type: 'delete', error });
      }
      this.onDeleted(id);
    },
  }),
};

export default Snippets;
